/**
 * EnhancedEnemySprites.ts
 * FF1-style enemy sprites with detailed pixel art
 *
 * 43 unique enemy types matching EnemyData.sol
 * Sizes: 32x32 (standard), 48x48 (large), 64x64 (bosses)
 */

export class EnhancedEnemySprites {
  /**
   * Generate all enemy sprites on a 256x256 canvas
   */
  generateEnemies(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, 256, 256);

    // Generate enemies in 8x8 grid (32x32 tiles)
    // IDs 1-43 map to different positions
    this.drawGoblin(ctx, 0, 0);        // ID 1
    this.drawWolf(ctx, 32, 0);         // ID 2
    this.drawZombie(ctx, 64, 0);       // ID 3
    this.drawSkeleton(ctx, 96, 0);     // ID 4
    this.drawPirate(ctx, 128, 0);      // ID 5
    this.drawShark(ctx, 160, 0);       // ID 6
    this.drawSpider(ctx, 192, 0);      // ID 7
    this.drawSnake(ctx, 224, 0);       // ID 8

    this.drawScorpion(ctx, 0, 32);     // ID 9
    this.drawImp(ctx, 32, 32);         // ID 10
    this.drawOgre(ctx, 64, 32);        // ID 11
    this.drawGiant(ctx, 96, 32);       // ID 12
    this.drawTroll(ctx, 128, 32);      // ID 13
    this.drawVampire(ctx, 160, 32);    // ID 14
    this.drawMummy(ctx, 192, 32);      // ID 15
    this.drawWraith(ctx, 224, 32);     // ID 16

    this.drawSpecter(ctx, 0, 64);      // ID 17
    this.drawMindflayer(ctx, 32, 64);  // ID 18
    this.drawManticore(ctx, 64, 64);   // ID 19
    this.drawChimera(ctx, 96, 64);     // ID 20
    this.drawMedusa(ctx, 128, 64);     // ID 21
    this.drawCockatrice(ctx, 160, 64); // ID 22
    this.drawGargoyle(ctx, 192, 64);   // ID 23
    this.drawGolem(ctx, 224, 64);      // ID 24

    this.drawDragonZombie(ctx, 0, 96);    // ID 25
    this.drawBlueDragon(ctx, 32, 96);     // ID 26
    this.drawRedDragon(ctx, 64, 96);      // ID 27
    this.drawGreenDragon(ctx, 96, 96);    // ID 28
    this.drawTyrannosaur(ctx, 128, 96);   // ID 29
    this.drawIronGolem(ctx, 160, 96);     // ID 30
    this.drawDeathKnight(ctx, 192, 96);   // ID 31
    this.drawDarkWizard(ctx, 224, 96);    // ID 32

    this.drawWarmech(ctx, 0, 128);      // ID 33
    this.drawPhantom(ctx, 32, 128);     // ID 34
    this.drawDeathEye(ctx, 64, 128);    // ID 35
    this.drawEvilEye(ctx, 96, 128);     // ID 36
    this.drawSorcerer(ctx, 128, 128);   // ID 37
    this.drawDemon(ctx, 160, 128);      // ID 38
    this.drawLich(ctx, 192, 128);       // ID 39
    this.drawKary(ctx, 224, 128);       // ID 40

    this.drawKraken(ctx, 0, 160);       // ID 41
    this.drawTiamat(ctx, 64, 160);      // ID 42
    this.drawChaos(ctx, 128, 160);      // ID 43

    return canvas;
  }

  // =====================
  // EARLY GAME (1-10)
  // =====================

  private drawGoblin(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Small green humanoid with club
    // Body (green)
    ctx.fillStyle = '#55aa33';
    ctx.fillRect(x + 10, y + 12, 12, 12);

    // Head
    ctx.fillStyle = '#66bb44';
    ctx.fillRect(x + 11, y + 8, 10, 8);

    // Eyes (red)
    ctx.fillStyle = '#ff3333';
    ctx.fillRect(x + 13, y + 11, 2, 2);
    ctx.fillRect(x + 17, y + 11, 2, 2);

    // Ears (pointy)
    ctx.fillStyle = '#55aa33';
    ctx.fillRect(x + 9, y + 10, 2, 3);
    ctx.fillRect(x + 21, y + 10, 2, 3);

    // Club (brown)
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 22, y + 14, 3, 8);
    ctx.fillRect(x + 21, y + 12, 4, 3);

    // Legs
    ctx.fillStyle = '#447722';
    ctx.fillRect(x + 11, y + 24, 4, 5);
    ctx.fillRect(x + 17, y + 24, 4, 5);

    // Loincloth
    ctx.fillStyle = '#884422';
    ctx.fillRect(x + 11, y + 22, 10, 3);
  }

  private drawWolf(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Gray quadruped beast
    // Body
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 8, y + 14, 16, 10);

    // Head
    ctx.fillRect(x + 20, y + 10, 8, 8);

    // Snout
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 26, y + 12, 4, 4);

    // Eyes (yellow/fierce)
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 22, y + 12, 2, 2);
    ctx.fillRect(x + 25, y + 12, 2, 2);

    // Ears
    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 21, y + 8, 2, 3);
    ctx.fillRect(x + 25, y + 8, 2, 3);

    // Legs
    ctx.fillStyle = '#777777';
    ctx.fillRect(x + 10, y + 22, 3, 7);
    ctx.fillRect(x + 15, y + 22, 3, 7);
    ctx.fillRect(x + 20, y + 22, 3, 7);

    // Tail
    ctx.fillRect(x + 5, y + 16, 4, 3);

    // Teeth
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 27, y + 15, 1, 2);
    ctx.fillRect(x + 29, y + 15, 1, 2);
  }

  private drawZombie(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Undead shambling corpse
    // Body (pale green/gray)
    ctx.fillStyle = '#889977';
    ctx.fillRect(x + 10, y + 12, 12, 14);

    // Head
    ctx.fillStyle = '#99aa88';
    ctx.fillRect(x + 11, y + 6, 10, 8);

    // Hollow eyes (black)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 13, y + 9, 2, 3);
    ctx.fillRect(x + 17, y + 9, 2, 3);

    // Mouth (open/decayed)
    ctx.fillRect(x + 14, y + 12, 4, 2);

    // Torn clothes (dark)
    ctx.fillStyle = '#554433';
    ctx.fillRect(x + 10, y + 14, 5, 8);
    ctx.fillRect(x + 17, y + 14, 5, 8);

    // Arms (reaching out)
    ctx.fillStyle = '#889977';
    ctx.fillRect(x + 6, y + 14, 5, 3);
    ctx.fillRect(x + 21, y + 14, 5, 3);

    // Legs (stumbling)
    ctx.fillRect(x + 10, y + 24, 4, 6);
    ctx.fillRect(x + 16, y + 25, 4, 5);
  }

  private drawSkeleton(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Animated skeleton warrior
    // Bones (white/cream)
    ctx.fillStyle = '#eeeecc';

    // Skull
    ctx.fillRect(x + 12, y + 6, 8, 8);
    // Eye sockets
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 14, y + 8, 2, 2);
    ctx.fillRect(x + 17, y + 8, 2, 2);

    // Ribcage
    ctx.fillStyle = '#eeeecc';
    ctx.fillRect(x + 11, y + 14, 10, 8);
    // Ribs (darker lines)
    ctx.fillStyle = '#ccccaa';
    ctx.fillRect(x + 12, y + 15, 8, 1);
    ctx.fillRect(x + 12, y + 17, 8, 1);
    ctx.fillRect(x + 12, y + 19, 8, 1);

    // Spine
    ctx.fillRect(x + 15, y + 22, 2, 4);

    // Arms (bones)
    ctx.fillStyle = '#eeeecc';
    ctx.fillRect(x + 8, y + 15, 4, 2);
    ctx.fillRect(x + 20, y + 15, 4, 2);

    // Sword (one arm)
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 21, y + 12, 2, 10);
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 20, y + 20, 4, 2);

    // Legs (bones)
    ctx.fillStyle = '#eeeecc';
    ctx.fillRect(x + 12, y + 24, 3, 6);
    ctx.fillRect(x + 17, y + 24, 3, 6);
  }

  private drawPirate(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Human pirate with bandana
    // Body (shirt)
    ctx.fillStyle = '#aa6644';
    ctx.fillRect(x + 10, y + 14, 12, 10);

    // Head (skin)
    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 12, y + 8, 8, 7);

    // Bandana (red)
    ctx.fillStyle = '#cc3333';
    ctx.fillRect(x + 11, y + 6, 10, 3);

    // Eyes
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 14, y + 10, 1, 1);
    // Eye patch
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 17, y + 10, 2, 2);

    // Beard
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 13, y + 13, 6, 3);

    // Belt
    ctx.fillStyle = '#884422';
    ctx.fillRect(x + 10, y + 20, 12, 2);

    // Cutlass
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 22, y + 12, 2, 8);
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 21, y + 18, 4, 2);

    // Legs (pants)
    ctx.fillStyle = '#444466';
    ctx.fillRect(x + 11, y + 24, 4, 6);
    ctx.fillRect(x + 17, y + 24, 4, 6);
  }

  private drawShark(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Menacing sea predator
    // Body (gray-blue)
    ctx.fillStyle = '#6688aa';
    ctx.fillRect(x + 6, y + 14, 20, 8);

    // Head/snout
    ctx.fillRect(x + 22, y + 14, 6, 8);

    // Belly (lighter)
    ctx.fillStyle = '#99bbdd';
    ctx.fillRect(x + 8, y + 18, 16, 4);

    // Dorsal fin
    ctx.fillStyle = '#556688';
    ctx.fillRect(x + 14, y + 8, 4, 7);
    ctx.fillRect(x + 15, y + 6, 2, 2);

    // Tail
    ctx.fillRect(x + 4, y + 12, 4, 4);
    ctx.fillRect(x + 2, y + 10, 3, 8);

    // Eye
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 24, y + 16, 2, 2);
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 25, y + 17, 1, 1);

    // Teeth (white, scary)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 26, y + 19, 1, 2);
    ctx.fillRect(x + 27, y + 19, 1, 2);
  }

  private drawSpider(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Giant spider
    // Body (black/brown)
    ctx.fillStyle = '#442222';
    ctx.fillRect(x + 12, y + 14, 8, 8);

    // Abdomen (larger)
    ctx.fillRect(x + 10, y + 20, 12, 8);

    // Head
    ctx.fillStyle = '#553333';
    ctx.fillRect(x + 13, y + 11, 6, 4);

    // Eyes (multiple, red)
    ctx.fillStyle = '#ff3333';
    ctx.fillRect(x + 14, y + 12, 1, 1);
    ctx.fillRect(x + 16, y + 12, 1, 1);
    ctx.fillRect(x + 18, y + 12, 1, 1);

    // Fangs
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 14, y + 14, 1, 2);
    ctx.fillRect(x + 17, y + 14, 1, 2);

    // Legs (8 legs, thin)
    ctx.fillStyle = '#442222';
    // Left side
    ctx.fillRect(x + 6, y + 15, 7, 1);
    ctx.fillRect(x + 5, y + 18, 8, 1);
    ctx.fillRect(x + 6, y + 21, 7, 1);
    ctx.fillRect(x + 7, y + 24, 6, 1);
    // Right side
    ctx.fillRect(x + 19, y + 15, 7, 1);
    ctx.fillRect(x + 19, y + 18, 8, 1);
    ctx.fillRect(x + 19, y + 21, 7, 1);
    ctx.fillRect(x + 19, y + 24, 6, 1);
  }

  private drawSnake(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Serpent, coiled and ready to strike
    // Body (green scales)
    ctx.fillStyle = '#44aa44';

    // Coiled body (S-curve)
    ctx.fillRect(x + 12, y + 24, 4, 6);
    ctx.fillRect(x + 14, y + 20, 6, 4);
    ctx.fillRect(x + 16, y + 16, 6, 4);
    ctx.fillRect(x + 14, y + 12, 6, 4);
    ctx.fillRect(x + 12, y + 8, 4, 4);

    // Head (raised, ready to strike)
    ctx.fillStyle = '#55bb55';
    ctx.fillRect(x + 10, y + 4, 6, 6);

    // Eyes (yellow/slit)
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 11, y + 6, 1, 2);
    ctx.fillRect(x + 14, y + 6, 1, 2);

    // Tongue (forked, red)
    ctx.fillStyle = '#ff3333';
    ctx.fillRect(x + 12, y + 8, 1, 2);
    ctx.fillRect(x + 12, y + 9, 2, 1);

    // Scales (darker stripes)
    ctx.fillStyle = '#338833';
    ctx.fillRect(x + 13, y + 22, 3, 1);
    ctx.fillRect(x + 15, y + 18, 4, 1);
    ctx.fillRect(x + 17, y + 14, 4, 1);
  }

  private drawScorpion(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Desert scorpion with tail
    // Body (brown/tan)
    ctx.fillStyle = '#aa8844';
    ctx.fillRect(x + 12, y + 16, 10, 6);

    // Head
    ctx.fillRect(x + 20, y + 16, 4, 6);

    // Pincers
    ctx.fillRect(x + 22, y + 14, 3, 2);
    ctx.fillRect(x + 22, y + 20, 3, 2);
    ctx.fillRect(x + 24, y + 13, 2, 3);
    ctx.fillRect(x + 24, y + 20, 2, 3);

    // Legs (multiple segments)
    ctx.fillStyle = '#886622';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(x + 8, y + 17 + i, 5, 1);
      ctx.fillRect(x + 21, y + 17 + i, 5, 1);
    }

    // Tail (curved up, deadly)
    ctx.fillStyle = '#aa8844';
    ctx.fillRect(x + 10, y + 18, 2, 4);
    ctx.fillRect(x + 8, y + 14, 2, 4);
    ctx.fillRect(x + 6, y + 10, 2, 4);

    // Stinger (black, poisonous)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 5, y + 8, 2, 3);
  }

  private drawImp(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Small red demon with wings
    // Body (red)
    ctx.fillStyle = '#ff3333';
    ctx.fillRect(x + 12, y + 14, 8, 10);

    // Head (with horns)
    ctx.fillRect(x + 13, y + 10, 6, 5);

    // Horns
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(x + 12, y + 8, 2, 3);
    ctx.fillRect(x + 18, y + 8, 2, 3);
    ctx.fillRect(x + 11, y + 6, 2, 2);
    ctx.fillRect(x + 19, y + 6, 2, 2);

    // Eyes (yellow, glowing)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 14, y + 12, 1, 1);
    ctx.fillRect(x + 17, y + 12, 1, 1);

    // Wings (bat-like)
    ctx.fillStyle = '#aa2222';
    ctx.fillRect(x + 8, y + 15, 5, 4);
    ctx.fillRect(x + 6, y + 17, 3, 2);
    ctx.fillRect(x + 19, y + 15, 5, 4);
    ctx.fillRect(x + 23, y + 17, 3, 2);

    // Tail (pointed)
    ctx.fillRect(x + 14, y + 24, 2, 4);
    ctx.fillRect(x + 13, y + 27, 1, 2);

    // Pitchfork
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 21, y + 12, 1, 10);
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 19, y + 10, 5, 2);
    ctx.fillRect(x + 20, y + 8, 1, 2);
    ctx.fillRect(x + 22, y + 8, 1, 2);
  }

  // =====================
  // MID GAME (11-24)
  // =====================

  private drawOgre(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Large brutish humanoid
    // Body (muscular, tan-brown)
    ctx.fillStyle = '#aa7744';
    ctx.fillRect(x + 8, y + 14, 16, 14);

    // Head (large, brutish)
    ctx.fillStyle = '#bb8855';
    ctx.fillRect(x + 10, y + 6, 12, 10);

    // Eyes (small, beady)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 13, y + 10, 2, 2);
    ctx.fillRect(x + 17, y + 10, 2, 2);

    // Tusks (protruding)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 14, 2, 3);
    ctx.fillRect(x + 18, y + 14, 2, 3);

    // Arms (massive)
    ctx.fillStyle = '#aa7744';
    ctx.fillRect(x + 4, y + 16, 5, 8);
    ctx.fillRect(x + 23, y + 16, 5, 8);

    // Fists
    ctx.fillStyle = '#996633';
    ctx.fillRect(x + 3, y + 23, 4, 4);
    ctx.fillRect(x + 25, y + 23, 4, 4);

    // Legs (thick)
    ctx.fillRect(x + 9, y + 26, 6, 5);
    ctx.fillRect(x + 17, y + 26, 6, 5);

    // Belt
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 8, y + 24, 16, 2);
  }

  private drawGiant(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Massive humanoid
    // Body (large build)
    ctx.fillStyle = '#cc9966';
    ctx.fillRect(x + 7, y + 12, 18, 16);

    // Head
    ctx.fillStyle = '#ddaa77';
    ctx.fillRect(x + 9, y + 4, 14, 10);

    // Hair/beard
    ctx.fillStyle = '#886644';
    ctx.fillRect(x + 8, y + 3, 16, 3);
    ctx.fillRect(x + 10, y + 12, 12, 3);

    // Eyes
    ctx.fillStyle = '#0088ff';
    ctx.fillRect(x + 12, y + 8, 2, 2);
    ctx.fillRect(x + 18, y + 8, 2, 2);

    // Armor (chest plate)
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 9, y + 14, 14, 8);

    // Belt
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 8, y + 22, 16, 2);

    // Legs
    ctx.fillStyle = '#cc9966';
    ctx.fillRect(x + 9, y + 26, 6, 5);
    ctx.fillRect(x + 17, y + 26, 6, 5);

    // Club (massive)
    ctx.fillStyle = '#885522';
    ctx.fillRect(x + 25, y + 8, 4, 16);
    ctx.fillRect(x + 24, y + 6, 6, 4);
  }

  private drawTroll(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Regenerating beast
    // Body (green-skinned)
    ctx.fillStyle = '#66aa66';
    ctx.fillRect(x + 9, y + 14, 14, 14);

    // Head (hunched forward)
    ctx.fillStyle = '#77bb77';
    ctx.fillRect(x + 11, y + 8, 10, 8);

    // Eyes (yellow, menacing)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 13, y + 11, 2, 2);
    ctx.fillRect(x + 17, y + 11, 2, 2);

    // Nose (large)
    ctx.fillStyle = '#559955';
    ctx.fillRect(x + 15, y + 13, 2, 3);

    // Claws (sharp)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 5, y + 18, 2, 1);
    ctx.fillRect(x + 6, y + 19, 1, 2);
    ctx.fillRect(x + 25, y + 18, 2, 1);
    ctx.fillRect(x + 25, y + 19, 1, 2);

    // Arms (long, apelike)
    ctx.fillStyle = '#66aa66';
    ctx.fillRect(x + 5, y + 16, 5, 6);
    ctx.fillRect(x + 22, y + 16, 5, 6);

    // Legs (hunched)
    ctx.fillRect(x + 10, y + 26, 5, 5);
    ctx.fillRect(x + 17, y + 26, 5, 5);

    // Wounds/scars (regenerating)
    ctx.fillStyle = '#ff3333';
    ctx.fillRect(x + 12, y + 16, 2, 1);
    ctx.fillRect(x + 18, y + 20, 2, 1);
  }

  private drawVampire(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Undead bloodsucker
    // Body (cape/robe)
    ctx.fillStyle = '#330033';
    ctx.fillRect(x + 7, y + 14, 18, 15);

    // Inner cape (red lining)
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(x + 10, y + 16, 4, 10);
    ctx.fillRect(x + 18, y + 16, 4, 10);

    // Head (pale)
    ctx.fillStyle = '#ffccdd';
    ctx.fillRect(x + 11, y + 6, 10, 10);

    // Hair (slicked back)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 10, y + 4, 12, 4);

    // Eyes (red, glowing)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 13, y + 10, 2, 2);
    ctx.fillRect(x + 17, y + 10, 2, 2);

    // Fangs
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 13, y + 14, 1, 2);
    ctx.fillRect(x + 18, y + 14, 1, 2);

    // Collar (high, dramatic)
    ctx.fillStyle = '#330033';
    ctx.fillRect(x + 8, y + 12, 3, 6);
    ctx.fillRect(x + 21, y + 12, 3, 6);

    // Hands (pale)
    ctx.fillStyle = '#ffccdd';
    ctx.fillRect(x + 8, y + 20, 3, 4);
    ctx.fillRect(x + 21, y + 20, 3, 4);
  }

  private drawMummy(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Wrapped undead
    // Body (bandages)
    ctx.fillStyle = '#ddccaa';
    ctx.fillRect(x + 10, y + 12, 12, 16);

    // Head (wrapped)
    ctx.fillRect(x + 11, y + 6, 10, 8);

    // Bandage wrappings (darker lines)
    ctx.fillStyle = '#ccbb99';
    // Head wraps
    ctx.fillRect(x + 10, y + 8, 12, 1);
    ctx.fillRect(x + 10, y + 10, 12, 1);
    ctx.fillRect(x + 10, y + 12, 12, 1);
    // Body wraps
    ctx.fillRect(x + 9, y + 15, 14, 1);
    ctx.fillRect(x + 9, y + 18, 14, 1);
    ctx.fillRect(x + 9, y + 21, 14, 1);
    ctx.fillRect(x + 9, y + 24, 14, 1);

    // Eyes (glowing through wraps)
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 13, y + 9, 2, 2);
    ctx.fillRect(x + 17, y + 9, 2, 2);

    // Arms (outstretched)
    ctx.fillStyle = '#ddccaa';
    ctx.fillRect(x + 6, y + 14, 5, 3);
    ctx.fillRect(x + 21, y + 14, 5, 3);

    // Legs (shambling)
    ctx.fillRect(x + 11, y + 26, 4, 5);
    ctx.fillRect(x + 17, y + 26, 4, 5);

    // Torn bandages
    ctx.fillStyle = '#998877';
    ctx.fillRect(x + 12, y + 27, 1, 2);
    ctx.fillRect(x + 19, y + 28, 1, 2);
  }

  private drawWraith(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Dark ghost
    // Body (ethereal, dark)
    ctx.fillStyle = '#6666aa';
    ctx.fillRect(x + 10, y + 10, 12, 16);

    // Wispy edges
    ctx.fillStyle = '#5555aa';
    ctx.fillRect(x + 8, y + 12, 2, 10);
    ctx.fillRect(x + 22, y + 12, 2, 10);
    ctx.fillRect(x + 9, y + 24, 4, 2);
    ctx.fillRect(x + 19, y + 24, 4, 2);

    // Head/hood
    ctx.fillStyle = '#7777bb';
    ctx.fillRect(x + 11, y + 6, 10, 6);

    // Hood shadow
    ctx.fillStyle = '#333366';
    ctx.fillRect(x + 12, y + 8, 8, 4);

    // Eyes (glowing white)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 13, y + 9, 2, 2);
    ctx.fillRect(x + 17, y + 9, 2, 2);

    // Wispy tail
    ctx.fillStyle = '#5555aa';
    ctx.fillRect(x + 13, y + 24, 6, 2);
    ctx.fillRect(x + 14, y + 26, 4, 2);
    ctx.fillRect(x + 15, y + 28, 2, 2);

    // Tattered cloak edges
    ctx.fillStyle = '#444488';
    ctx.fillRect(x + 9, y + 20, 2, 1);
    ctx.fillRect(x + 21, y + 20, 2, 1);
  }

  private drawSpecter(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Light ghost/spirit
    // Body (ethereal, light)
    ctx.fillStyle = '#aaaaee';
    ctx.fillRect(x + 10, y + 10, 12, 16);

    // Glow effect
    ctx.fillStyle = '#ccccff';
    ctx.fillRect(x + 11, y + 11, 10, 14);

    // Wispy edges
    ctx.fillStyle = '#9999dd';
    ctx.fillRect(x + 8, y + 12, 2, 10);
    ctx.fillRect(x + 22, y + 12, 2, 10);
    ctx.fillRect(x + 9, y + 24, 4, 2);
    ctx.fillRect(x + 19, y + 24, 4, 2);

    // Head
    ctx.fillStyle = '#bbbbff';
    ctx.fillRect(x + 12, y + 6, 8, 6);

    // Eyes (hollow darkness)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 13, y + 9, 2, 3);
    ctx.fillRect(x + 17, y + 9, 2, 3);

    // Mouth (wailing)
    ctx.fillRect(x + 15, y + 11, 2, 2);

    // Ethereal wisps
    ctx.fillStyle = '#9999dd';
    ctx.fillRect(x + 13, y + 24, 6, 2);
    ctx.fillRect(x + 14, y + 26, 4, 2);
    ctx.fillRect(x + 15, y + 28, 2, 1);
  }

  private drawMindflayer(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Psionic horror with tentacles
    // Body (robes)
    ctx.fillStyle = '#6644aa';
    ctx.fillRect(x + 9, y + 14, 14, 14);

    // Head (bulbous)
    ctx.fillStyle = '#8866cc';
    ctx.fillRect(x + 10, y + 6, 12, 10);

    // Brain bulges
    ctx.fillStyle = '#9977dd';
    ctx.fillRect(x + 11, y + 5, 4, 2);
    ctx.fillRect(x + 17, y + 5, 4, 2);

    // Eyes (large, alien)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 9, 3, 3);
    ctx.fillRect(x + 17, y + 9, 3, 3);
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 13, y + 10, 1, 1);
    ctx.fillRect(x + 18, y + 10, 1, 1);

    // Tentacles (face)
    ctx.fillStyle = '#7755bb';
    ctx.fillRect(x + 11, y + 14, 2, 4);
    ctx.fillRect(x + 14, y + 14, 2, 4);
    ctx.fillRect(x + 17, y + 14, 2, 4);
    ctx.fillRect(x + 19, y + 14, 2, 4);

    // Arms
    ctx.fillStyle = '#6644aa';
    ctx.fillRect(x + 6, y + 16, 4, 6);
    ctx.fillRect(x + 22, y + 16, 4, 6);

    // Hands (three-fingered)
    ctx.fillStyle = '#8866cc';
    ctx.fillRect(x + 5, y + 21, 2, 2);
    ctx.fillRect(x + 24, y + 21, 2, 2);
  }

  private drawManticore(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Lion body, dragon wings, scorpion tail
    // Body (lion)
    ctx.fillStyle = '#cc8844';
    ctx.fillRect(x + 8, y + 16, 16, 10);

    // Head (lion with mane)
    ctx.fillStyle = '#dd9955';
    ctx.fillRect(x + 20, y + 12, 8, 8);

    // Mane
    ctx.fillStyle = '#aa6622';
    ctx.fillRect(x + 19, y + 10, 10, 2);
    ctx.fillRect(x + 18, y + 12, 2, 6);
    ctx.fillRect(x + 28, y + 12, 2, 6);

    // Eyes (fierce)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 22, y + 14, 2, 2);
    ctx.fillRect(x + 25, y + 14, 2, 2);

    // Wings (bat-like)
    ctx.fillStyle = '#996633';
    ctx.fillRect(x + 4, y + 14, 5, 6);
    ctx.fillRect(x + 2, y + 16, 3, 4);
    ctx.fillRect(x + 23, y + 14, 5, 6);

    // Legs
    ctx.fillStyle = '#bb7733';
    ctx.fillRect(x + 10, y + 24, 3, 6);
    ctx.fillRect(x + 15, y + 24, 3, 6);
    ctx.fillRect(x + 20, y + 24, 3, 6);

    // Scorpion tail
    ctx.fillStyle = '#cc8844';
    ctx.fillRect(x + 6, y + 18, 2, 4);
    ctx.fillRect(x + 4, y + 14, 2, 4);
    ctx.fillRect(x + 3, y + 10, 2, 4);

    // Stinger
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 2, y + 8, 2, 3);
  }

  private drawChimera(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Multi-headed beast (lion, goat, dragon)
    // Body (large, muscular)
    ctx.fillStyle = '#aa6644';
    ctx.fillRect(x + 7, y + 16, 18, 12);

    // Lion head (front)
    ctx.fillStyle = '#cc8855';
    ctx.fillRect(x + 20, y + 10, 8, 8);
    // Lion mane
    ctx.fillStyle = '#994422';
    ctx.fillRect(x + 19, y + 8, 10, 2);
    ctx.fillRect(x + 18, y + 10, 2, 6);

    // Goat head (back/top)
    ctx.fillStyle = '#dddddd';
    ctx.fillRect(x + 10, y + 8, 6, 6);
    // Goat horns
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 9, y + 6, 2, 3);
    ctx.fillRect(x + 15, y + 6, 2, 3);

    // Dragon tail/head
    ctx.fillStyle = '#66aa44';
    ctx.fillRect(x + 4, y + 18, 4, 4);
    // Dragon head on tail
    ctx.fillRect(x + 2, y + 16, 3, 3);
    ctx.fillStyle = '#ff6600';
    ctx.fillRect(x + 1, y + 17, 1, 1);

    // Legs (powerful)
    ctx.fillStyle = '#aa6644';
    ctx.fillRect(x + 9, y + 26, 4, 5);
    ctx.fillRect(x + 15, y + 26, 4, 5);
    ctx.fillRect(x + 21, y + 26, 4, 5);

    // Eyes
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 22, y + 12, 2, 2);
    ctx.fillRect(x + 11, y + 10, 1, 1);
  }

  private drawMedusa(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Snake-haired gorgon
    // Body (humanoid, scaled)
    ctx.fillStyle = '#66aa88';
    ctx.fillRect(x + 10, y + 14, 12, 14);

    // Head/face
    ctx.fillStyle = '#77bb99';
    ctx.fillRect(x + 11, y + 6, 10, 10);

    // Snake hair (multiple snakes)
    ctx.fillStyle = '#559977';
    // Multiple snake strands
    ctx.fillRect(x + 9, y + 4, 2, 6);
    ctx.fillRect(x + 12, y + 3, 2, 5);
    ctx.fillRect(x + 15, y + 2, 2, 6);
    ctx.fillRect(x + 18, y + 3, 2, 5);
    ctx.fillRect(x + 21, y + 4, 2, 6);

    // Snake heads
    ctx.fillStyle = '#44aa66';
    ctx.fillRect(x + 9, y + 2, 2, 2);
    ctx.fillRect(x + 12, y + 1, 2, 2);
    ctx.fillRect(x + 15, y + 0, 2, 2);
    ctx.fillRect(x + 18, y + 1, 2, 2);
    ctx.fillRect(x + 21, y + 2, 2, 2);

    // Eyes (petrifying gaze)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 13, y + 10, 2, 2);
    ctx.fillRect(x + 17, y + 10, 2, 2);

    // Arms
    ctx.fillStyle = '#66aa88';
    ctx.fillRect(x + 6, y + 16, 5, 6);
    ctx.fillRect(x + 21, y + 16, 5, 6);

    // Claws
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 5, y + 21, 2, 1);
    ctx.fillRect(x + 25, y + 21, 2, 1);

    // Lower body (snake tail)
    ctx.fillRect(x + 11, y + 26, 10, 3);
    ctx.fillRect(x + 13, y + 28, 6, 2);
  }

  private drawCockatrice(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Rooster-dragon hybrid
    // Body (chicken-like)
    ctx.fillStyle = '#aa8833';
    ctx.fillRect(x + 12, y + 16, 10, 10);

    // Head (rooster)
    ctx.fillStyle = '#cc9944';
    ctx.fillRect(x + 18, y + 10, 8, 8);

    // Comb (rooster crest)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 20, y + 6, 2, 4);
    ctx.fillRect(x + 22, y + 5, 2, 5);
    ctx.fillRect(x + 24, y + 6, 2, 4);

    // Beak
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 26, y + 13, 3, 2);

    // Eye
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 22, y + 12, 2, 2);

    // Wings (feathered)
    ctx.fillStyle = '#996622';
    ctx.fillRect(x + 8, y + 18, 5, 6);
    ctx.fillRect(x + 6, y + 20, 3, 4);
    ctx.fillRect(x + 21, y + 18, 5, 6);

    // Tail feathers
    ctx.fillRect(x + 10, y + 24, 2, 4);
    ctx.fillRect(x + 12, y + 25, 2, 4);
    ctx.fillRect(x + 14, y + 26, 2, 3);

    // Legs (scaly)
    ctx.fillStyle = '#bbaa44';
    ctx.fillRect(x + 15, y + 24, 2, 6);
    ctx.fillRect(x + 19, y + 24, 2, 6);

    // Claws
    ctx.fillStyle = '#886622';
    ctx.fillRect(x + 14, y + 29, 4, 1);
    ctx.fillRect(x + 18, y + 29, 4, 1);
  }

  private drawGargoyle(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Stone demon
    // Body (stone gray)
    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 10, y + 14, 12, 14);

    // Head (demonic)
    ctx.fillStyle = '#777777';
    ctx.fillRect(x + 11, y + 8, 10, 8);

    // Horns
    ctx.fillStyle = '#555555';
    ctx.fillRect(x + 10, y + 6, 2, 4);
    ctx.fillRect(x + 20, y + 6, 2, 4);
    ctx.fillRect(x + 9, y + 4, 2, 3);
    ctx.fillRect(x + 21, y + 4, 2, 3);

    // Eyes (glowing red)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 13, y + 11, 2, 2);
    ctx.fillRect(x + 17, y + 11, 2, 2);

    // Wings (stone, bat-like)
    ctx.fillStyle = '#555555';
    ctx.fillRect(x + 5, y + 12, 6, 8);
    ctx.fillRect(x + 3, y + 14, 3, 6);
    ctx.fillRect(x + 21, y + 12, 6, 8);
    ctx.fillRect(x + 26, y + 14, 3, 6);

    // Wing details
    ctx.fillStyle = '#444444';
    ctx.fillRect(x + 6, y + 15, 1, 4);
    ctx.fillRect(x + 8, y + 14, 1, 5);
    ctx.fillRect(x + 23, y + 15, 1, 4);
    ctx.fillRect(x + 25, y + 14, 1, 5);

    // Claws (feet)
    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 11, y + 26, 4, 5);
    ctx.fillRect(x + 17, y + 26, 4, 5);

    // Stone texture
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 12, y + 16, 2, 2);
    ctx.fillRect(x + 16, y + 20, 2, 2);
  }

  private drawGolem(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Stone/earth construct
    // Body (large, blocky)
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 8, y + 12, 16, 16);

    // Head (cubic)
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 10, y + 4, 12, 10);

    // Eyes (glowing)
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 12, y + 8, 3, 3);
    ctx.fillRect(x + 17, y + 8, 3, 3);

    // Stone blocks/segments
    ctx.fillStyle = '#777777';
    ctx.fillRect(x + 8, y + 20, 16, 1);
    ctx.fillRect(x + 8, y + 24, 16, 1);
    ctx.fillRect(x + 15, y + 12, 1, 16);

    // Cracks in stone
    ctx.fillStyle = '#555555';
    ctx.fillRect(x + 11, y + 6, 1, 3);
    ctx.fillRect(x + 13, y + 14, 2, 1);
    ctx.fillRect(x + 18, y + 18, 3, 1);

    // Arms (massive)
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 4, y + 14, 5, 10);
    ctx.fillRect(x + 23, y + 14, 5, 10);

    // Fists
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 3, y + 23, 4, 5);
    ctx.fillRect(x + 25, y + 23, 4, 5);

    // Legs
    ctx.fillRect(x + 9, y + 26, 6, 5);
    ctx.fillRect(x + 17, y + 26, 6, 5);

    // Rune on chest
    ctx.fillStyle = '#00ffff';
    ctx.fillRect(x + 14, y + 18, 4, 1);
    ctx.fillRect(x + 15, y + 16, 2, 4);
  }

  // =====================
  // LATE GAME (25-38)
  // =====================

  private drawDragonZombie(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Undead dragon (skeletal)
    // Body (bones)
    ctx.fillStyle = '#668866';
    ctx.fillRect(x + 6, y + 14, 18, 10);

    // Ribs showing
    ctx.fillStyle = '#557755';
    ctx.fillRect(x + 8, y + 16, 14, 1);
    ctx.fillRect(x + 8, y + 18, 14, 1);
    ctx.fillRect(x + 8, y + 20, 14, 1);

    // Head/skull
    ctx.fillStyle = '#779977';
    ctx.fillRect(x + 20, y + 10, 10, 8);

    // Jaw (open)
    ctx.fillStyle = '#668866';
    ctx.fillRect(x + 22, y + 16, 6, 3);

    // Eye sockets (glowing)
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(x + 23, y + 12, 2, 2);
    ctx.fillRect(x + 27, y + 12, 2, 2);

    // Horns
    ctx.fillStyle = '#557755';
    ctx.fillRect(x + 21, y + 8, 2, 3);
    ctx.fillRect(x + 27, y + 8, 2, 3);

    // Wings (tattered, skeletal)
    ctx.fillStyle = '#446644';
    ctx.fillRect(x + 2, y + 12, 5, 8);
    ctx.fillRect(x + 25, y + 12, 5, 8);
    // Wing bones
    ctx.fillStyle = '#557755';
    ctx.fillRect(x + 3, y + 14, 1, 5);
    ctx.fillRect(x + 27, y + 14, 1, 5);

    // Tail (bony)
    ctx.fillStyle = '#668866';
    ctx.fillRect(x + 4, y + 18, 3, 3);
    ctx.fillRect(x + 2, y + 20, 3, 2);

    // Legs
    ctx.fillRect(x + 10, y + 22, 3, 6);
    ctx.fillRect(x + 18, y + 22, 3, 6);
  }

  private drawBlueDragon(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Lightning dragon
    // Body (blue scales)
    ctx.fillStyle = '#4488ff';
    ctx.fillRect(x + 6, y + 14, 18, 10);

    // Scales (lighter)
    ctx.fillStyle = '#5599ff';
    ctx.fillRect(x + 8, y + 16, 2, 2);
    ctx.fillRect(x + 12, y + 18, 2, 2);
    ctx.fillRect(x + 16, y + 16, 2, 2);
    ctx.fillRect(x + 20, y + 18, 2, 2);

    // Head
    ctx.fillStyle = '#5599ff';
    ctx.fillRect(x + 20, y + 9, 10, 9);

    // Snout
    ctx.fillRect(x + 26, y + 12, 4, 4);

    // Eyes (yellow)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 22, y + 12, 2, 2);
    ctx.fillRect(x + 26, y + 12, 2, 2);

    // Horns (swept back)
    ctx.fillStyle = '#3366cc';
    ctx.fillRect(x + 21, y + 7, 2, 3);
    ctx.fillRect(x + 25, y + 7, 2, 3);
    ctx.fillRect(x + 20, y + 5, 3, 2);
    ctx.fillRect(x + 26, y + 5, 3, 2);

    // Wings (majestic)
    ctx.fillStyle = '#3377dd';
    ctx.fillRect(x + 2, y + 10, 6, 10);
    ctx.fillRect(x + 0, y + 12, 3, 8);
    ctx.fillRect(x + 24, y + 10, 6, 10);

    // Wing membranes
    ctx.fillStyle = '#5599ff';
    ctx.fillRect(x + 3, y + 13, 4, 6);
    ctx.fillRect(x + 25, y + 13, 4, 6);

    // Tail
    ctx.fillStyle = '#4488ff';
    ctx.fillRect(x + 4, y + 18, 3, 3);
    ctx.fillRect(x + 2, y + 20, 3, 2);

    // Legs
    ctx.fillRect(x + 10, y + 22, 3, 6);
    ctx.fillRect(x + 18, y + 22, 3, 6);

    // Lightning effect
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 28, y + 14, 1, 1);
    ctx.fillRect(x + 29, y + 15, 1, 1);
  }

  private drawRedDragon(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Fire dragon
    // Body (red scales)
    ctx.fillStyle = '#ff4400';
    ctx.fillRect(x + 6, y + 14, 18, 10);

    // Scales (orange)
    ctx.fillStyle = '#ff6622';
    ctx.fillRect(x + 8, y + 16, 2, 2);
    ctx.fillRect(x + 12, y + 18, 2, 2);
    ctx.fillRect(x + 16, y + 16, 2, 2);
    ctx.fillRect(x + 20, y + 18, 2, 2);

    // Head
    ctx.fillStyle = '#ff5511';
    ctx.fillRect(x + 20, y + 9, 10, 9);

    // Snout
    ctx.fillRect(x + 26, y + 12, 4, 4);

    // Eyes (yellow/fierce)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 22, y + 12, 2, 2);
    ctx.fillRect(x + 26, y + 12, 2, 2);

    // Horns (curved)
    ctx.fillStyle = '#cc3300';
    ctx.fillRect(x + 21, y + 7, 2, 3);
    ctx.fillRect(x + 25, y + 7, 2, 3);
    ctx.fillRect(x + 20, y + 5, 3, 2);
    ctx.fillRect(x + 26, y + 5, 3, 2);

    // Wings (massive)
    ctx.fillStyle = '#dd3300';
    ctx.fillRect(x + 2, y + 10, 6, 10);
    ctx.fillRect(x + 0, y + 12, 3, 8);
    ctx.fillRect(x + 24, y + 10, 6, 10);

    // Wing membranes
    ctx.fillStyle = '#ff6622';
    ctx.fillRect(x + 3, y + 13, 4, 6);
    ctx.fillRect(x + 25, y + 13, 4, 6);

    // Spikes on back
    ctx.fillStyle = '#cc3300';
    ctx.fillRect(x + 10, y + 12, 2, 2);
    ctx.fillRect(x + 14, y + 11, 2, 3);
    ctx.fillRect(x + 18, y + 12, 2, 2);

    // Tail
    ctx.fillStyle = '#ff4400';
    ctx.fillRect(x + 4, y + 18, 3, 3);
    ctx.fillRect(x + 2, y + 20, 3, 2);

    // Legs
    ctx.fillRect(x + 10, y + 22, 3, 6);
    ctx.fillRect(x + 18, y + 22, 3, 6);

    // Fire breath
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 28, y + 14, 1, 1);
    ctx.fillRect(x + 29, y + 13, 1, 2);
  }

  private drawGreenDragon(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Poison dragon
    // Body (green scales)
    ctx.fillStyle = '#44aa44';
    ctx.fillRect(x + 6, y + 14, 18, 10);

    // Scales (lighter green)
    ctx.fillStyle = '#55bb55';
    ctx.fillRect(x + 8, y + 16, 2, 2);
    ctx.fillRect(x + 12, y + 18, 2, 2);
    ctx.fillRect(x + 16, y + 16, 2, 2);
    ctx.fillRect(x + 20, y + 18, 2, 2);

    // Head
    ctx.fillStyle = '#55bb55';
    ctx.fillRect(x + 20, y + 9, 10, 9);

    // Snout
    ctx.fillRect(x + 26, y + 12, 4, 4);

    // Eyes (yellow slits)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 22, y + 12, 2, 2);
    ctx.fillRect(x + 26, y + 12, 2, 2);

    // Horns
    ctx.fillStyle = '#338833';
    ctx.fillRect(x + 21, y + 7, 2, 3);
    ctx.fillRect(x + 25, y + 7, 2, 3);

    // Wings
    ctx.fillStyle = '#339933';
    ctx.fillRect(x + 2, y + 10, 6, 10);
    ctx.fillRect(x + 0, y + 12, 3, 8);
    ctx.fillRect(x + 24, y + 10, 6, 10);

    // Wing membranes
    ctx.fillStyle = '#55bb55';
    ctx.fillRect(x + 3, y + 13, 4, 6);
    ctx.fillRect(x + 25, y + 13, 4, 6);

    // Tail
    ctx.fillStyle = '#44aa44';
    ctx.fillRect(x + 4, y + 18, 3, 3);
    ctx.fillRect(x + 2, y + 20, 3, 2);

    // Legs
    ctx.fillRect(x + 10, y + 22, 3, 6);
    ctx.fillRect(x + 18, y + 22, 3, 6);

    // Poison drip
    ctx.fillStyle = '#88ff00';
    ctx.fillRect(x + 28, y + 16, 1, 2);
  }

  private drawTyrannosaur(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Prehistoric beast
    // Body (brown/tan)
    ctx.fillStyle = '#886644';
    ctx.fillRect(x + 8, y + 12, 16, 14);

    // Head (massive jaws)
    ctx.fillStyle = '#997755';
    ctx.fillRect(x + 20, y + 8, 10, 10);

    // Upper jaw
    ctx.fillRect(x + 26, y + 10, 4, 4);

    // Lower jaw (open)
    ctx.fillStyle = '#886644';
    ctx.fillRect(x + 26, y + 14, 4, 4);

    // Teeth (many sharp teeth)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 26, y + 13, 1, 1);
    ctx.fillRect(x + 27, y + 13, 1, 2);
    ctx.fillRect(x + 28, y + 13, 1, 1);
    ctx.fillRect(x + 29, y + 13, 1, 1);

    // Eye
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 23, y + 11, 2, 2);

    // Body scales
    ctx.fillStyle = '#775533';
    ctx.fillRect(x + 10, y + 14, 2, 2);
    ctx.fillRect(x + 14, y + 16, 2, 2);
    ctx.fillRect(x + 18, y + 18, 2, 2);

    // Small arms (T-Rex style)
    ctx.fillStyle = '#886644';
    ctx.fillRect(x + 18, y + 16, 3, 4);
    ctx.fillRect(x + 21, y + 19, 2, 1);

    // Powerful legs
    ctx.fillRect(x + 10, y + 24, 5, 7);
    ctx.fillRect(x + 17, y + 24, 5, 7);

    // Tail (thick)
    ctx.fillRect(x + 4, y + 16, 5, 4);
    ctx.fillRect(x + 2, y + 18, 3, 2);

    // Claws on feet
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 9, y + 30, 2, 1);
    ctx.fillRect(x + 16, y + 30, 2, 1);
  }

  private drawIronGolem(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Metal construct
    // Body (metallic)
    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 8, y + 12, 16, 16);

    // Head
    ctx.fillStyle = '#bbbbbb';
    ctx.fillRect(x + 10, y + 4, 12, 10);

    // Eyes (glowing blue)
    ctx.fillStyle = '#0088ff';
    ctx.fillRect(x + 12, y + 8, 3, 3);
    ctx.fillRect(x + 17, y + 8, 3, 3);

    // Metal plates
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 8, y + 20, 16, 1);
    ctx.fillRect(x + 8, y + 24, 16, 1);
    ctx.fillRect(x + 15, y + 12, 1, 16);

    // Bolts/rivets
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 10, y + 14, 1, 1);
    ctx.fillRect(x + 21, y + 14, 1, 1);
    ctx.fillRect(x + 10, y + 22, 1, 1);
    ctx.fillRect(x + 21, y + 22, 1, 1);

    // Chest grill
    ctx.fillStyle = '#777777';
    ctx.fillRect(x + 12, y + 18, 8, 1);
    ctx.fillRect(x + 12, y + 20, 8, 1);
    ctx.fillRect(x + 12, y + 22, 8, 1);

    // Arms (heavy)
    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 4, y + 14, 5, 10);
    ctx.fillRect(x + 23, y + 14, 5, 10);

    // Fists (spiked)
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 3, y + 23, 4, 5);
    ctx.fillRect(x + 25, y + 23, 4, 5);
    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 2, y + 24, 2, 1);
    ctx.fillRect(x + 28, y + 24, 2, 1);

    // Legs
    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 9, y + 26, 6, 5);
    ctx.fillRect(x + 17, y + 26, 6, 5);
  }

  private drawDeathKnight(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Dark armored warrior
    // Body (black armor)
    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 9, y + 14, 14, 14);

    // Helmet
    ctx.fillStyle = '#333355';
    ctx.fillRect(x + 11, y + 6, 10, 10);

    // Visor (glowing eyes)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 12, y + 10, 8, 4);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 13, y + 11, 2, 2);
    ctx.fillRect(x + 17, y + 11, 2, 2);

    // Horned helmet
    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 10, y + 5, 2, 4);
    ctx.fillRect(x + 20, y + 5, 2, 4);
    ctx.fillRect(x + 9, y + 3, 2, 3);
    ctx.fillRect(x + 21, y + 3, 2, 3);

    // Shoulder plates
    ctx.fillStyle = '#333355';
    ctx.fillRect(x + 6, y + 14, 4, 4);
    ctx.fillRect(x + 22, y + 14, 4, 4);

    // Chest armor (detailed)
    ctx.fillStyle = '#444466';
    ctx.fillRect(x + 11, y + 16, 10, 8);
    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 13, y + 18, 2, 4);
    ctx.fillRect(x + 17, y + 18, 2, 4);

    // Cape (tattered)
    ctx.fillStyle = '#110022';
    ctx.fillRect(x + 7, y + 18, 3, 10);
    ctx.fillRect(x + 22, y + 18, 3, 10);

    // Sword (dark blade)
    ctx.fillStyle = '#333333';
    ctx.fillRect(x + 23, y + 10, 2, 16);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 22, y + 8, 4, 3);

    // Legs (armored)
    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 10, y + 26, 5, 5);
    ctx.fillRect(x + 17, y + 26, 5, 5);
  }

  private drawDarkWizard(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Evil sorcerer
    // Robes (dark purple)
    ctx.fillStyle = '#442266';
    ctx.fillRect(x + 8, y + 14, 16, 14);

    // Hood
    ctx.fillStyle = '#553377';
    ctx.fillRect(x + 10, y + 6, 12, 10);

    // Hood shadow (face hidden)
    ctx.fillStyle = '#220044';
    ctx.fillRect(x + 11, y + 8, 10, 8);

    // Eyes (glowing purple)
    ctx.fillStyle = '#aa00ff';
    ctx.fillRect(x + 13, y + 11, 2, 2);
    ctx.fillRect(x + 17, y + 11, 2, 2);

    // Robe details (arcane symbols)
    ctx.fillStyle = '#8844aa';
    ctx.fillRect(x + 14, y + 18, 4, 1);
    ctx.fillRect(x + 15, y + 16, 2, 4);
    // Spiral pattern
    ctx.fillRect(x + 10, y + 20, 2, 2);
    ctx.fillRect(x + 20, y + 20, 2, 2);

    // Staff (magical)
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 24, y + 8, 2, 18);
    // Orb on top
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(x + 23, y + 5, 4, 4);
    // Glow
    ctx.fillStyle = '#aa00ff';
    ctx.fillRect(x + 22, y + 6, 1, 2);
    ctx.fillRect(x + 27, y + 6, 1, 2);

    // Hands (pale)
    ctx.fillStyle = '#ccccdd';
    ctx.fillRect(x + 7, y + 18, 3, 3);
    ctx.fillRect(x + 22, y + 22, 3, 3);

    // Magical aura
    ctx.fillStyle = '#8844aa';
    ctx.fillRect(x + 6, y + 16, 1, 1);
    ctx.fillRect(x + 25, y + 24, 1, 1);
  }

  private drawWarmech(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Mechanical war machine (rare enemy)
    // Body (metallic robot)
    ctx.fillStyle = '#6666aa';
    ctx.fillRect(x + 8, y + 10, 16, 18);

    // Head/cockpit
    ctx.fillStyle = '#7777bb';
    ctx.fillRect(x + 11, y + 6, 10, 6);

    // Visor (red)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 13, y + 8, 6, 2);

    // Shoulder cannons
    ctx.fillStyle = '#555588';
    ctx.fillRect(x + 6, y + 10, 3, 6);
    ctx.fillRect(x + 23, y + 10, 3, 6);
    // Cannon barrels
    ctx.fillStyle = '#333355';
    ctx.fillRect(x + 4, y + 12, 3, 2);
    ctx.fillRect(x + 25, y + 12, 3, 2);

    // Chest panel
    ctx.fillStyle = '#8888cc';
    ctx.fillRect(x + 12, y + 14, 8, 8);
    // Panel details
    ctx.fillStyle = '#5555aa';
    ctx.fillRect(x + 13, y + 15, 2, 2);
    ctx.fillRect(x + 17, y + 15, 2, 2);
    ctx.fillRect(x + 13, y + 19, 2, 2);
    ctx.fillRect(x + 17, y + 19, 2, 2);

    // Arms (mechanical)
    ctx.fillStyle = '#6666aa';
    ctx.fillRect(x + 5, y + 16, 4, 8);
    ctx.fillRect(x + 23, y + 16, 4, 8);

    // Claws/hands
    ctx.fillStyle = '#555588';
    ctx.fillRect(x + 4, y + 23, 3, 4);
    ctx.fillRect(x + 25, y + 23, 3, 4);

    // Legs (heavy)
    ctx.fillStyle = '#6666aa';
    ctx.fillRect(x + 10, y + 26, 5, 5);
    ctx.fillRect(x + 17, y + 26, 5, 5);

    // Thrusters
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 9, y + 28, 1, 1);
    ctx.fillRect(x + 18, y + 28, 1, 1);

    // Antenna
    ctx.fillStyle = '#7777bb';
    ctx.fillRect(x + 15, y + 4, 2, 3);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 15, y + 3, 2, 1);
  }

  private drawPhantom(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Powerful ghost
    // Body (ethereal white)
    ctx.fillStyle = '#ccccff';
    ctx.fillRect(x + 10, y + 8, 12, 18);

    // Inner glow
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 10, 8, 14);

    // Wispy edges (animated feel)
    ctx.fillStyle = '#aaaaee';
    ctx.fillRect(x + 8, y + 10, 2, 12);
    ctx.fillRect(x + 22, y + 10, 2, 12);
    ctx.fillRect(x + 9, y + 24, 4, 2);
    ctx.fillRect(x + 19, y + 24, 4, 2);
    ctx.fillRect(x + 11, y + 26, 3, 2);
    ctx.fillRect(x + 18, y + 26, 3, 2);

    // Head/face
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 4, 8, 6);

    // Eyes (dark voids)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 13, y + 6, 2, 3);
    ctx.fillRect(x + 17, y + 6, 2, 3);

    // Mouth (wailing)
    ctx.fillRect(x + 15, y + 8, 2, 3);

    // Ethereal wisps
    ctx.fillStyle = '#9999dd';
    ctx.fillRect(x + 7, y + 14, 2, 1);
    ctx.fillRect(x + 23, y + 16, 2, 1);
    ctx.fillRect(x + 13, y + 26, 6, 1);
    ctx.fillRect(x + 14, y + 28, 4, 1);
  }

  private drawDeathEye(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Beholder-like floating eye
    // Main body (eyeball)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 10, y + 10, 12, 12);

    // Iris (red/evil)
    ctx.fillStyle = '#aa0000';
    ctx.fillRect(x + 12, y + 12, 8, 8);

    // Pupil (black)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 14, y + 14, 4, 4);

    // Glint
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 15, y + 15, 1, 1);

    // Veins (bloodshot)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 11, y + 12, 1, 4);
    ctx.fillRect(x + 20, y + 14, 1, 3);
    ctx.fillRect(x + 13, y + 11, 3, 1);
    ctx.fillRect(x + 17, y + 20, 2, 1);

    // Eyestalks (smaller eyes)
    ctx.fillStyle = '#cc6666';
    ctx.fillRect(x + 8, y + 8, 2, 4);
    ctx.fillRect(x + 22, y + 8, 2, 4);
    ctx.fillRect(x + 6, y + 14, 2, 4);
    ctx.fillRect(x + 24, y + 14, 2, 4);
    ctx.fillRect(x + 8, y + 20, 2, 4);
    ctx.fillRect(x + 22, y + 20, 2, 4);

    // Small eyes on stalks
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 8, y + 7, 2, 2);
    ctx.fillRect(x + 22, y + 7, 2, 2);
    ctx.fillRect(x + 6, y + 13, 2, 2);
    ctx.fillRect(x + 24, y + 13, 2, 2);
    ctx.fillRect(x + 8, y + 19, 2, 2);
    ctx.fillRect(x + 22, y + 19, 2, 2);

    // Eye beams
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 7, y + 6, 1, 1);
    ctx.fillRect(x + 5, y + 12, 1, 1);
  }

  private drawEvilEye(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Lesser beholder
    // Main body (eyeball)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 11, y + 12, 10, 10);

    // Iris (dark red)
    ctx.fillStyle = '#880000';
    ctx.fillRect(x + 13, y + 14, 6, 6);

    // Pupil (black)
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 15, y + 16, 2, 2);

    // Glint
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 16, y + 16, 1, 1);

    // Veins
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(x + 12, y + 14, 1, 3);
    ctx.fillRect(x + 19, y + 16, 1, 2);
    ctx.fillRect(x + 14, y + 13, 2, 1);

    // Eyestalks (fewer than Death Eye)
    ctx.fillStyle = '#bb6666';
    ctx.fillRect(x + 9, y + 10, 2, 4);
    ctx.fillRect(x + 21, y + 10, 2, 4);
    ctx.fillRect(x + 7, y + 16, 2, 4);
    ctx.fillRect(x + 23, y + 16, 2, 4);

    // Small eyes on stalks
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 9, y + 9, 2, 2);
    ctx.fillRect(x + 21, y + 9, 2, 2);
    ctx.fillRect(x + 7, y + 15, 2, 2);
    ctx.fillRect(x + 23, y + 15, 2, 2);

    // Floating effect
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 13, y + 23, 6, 1);
    ctx.fillRect(x + 14, y + 24, 4, 1);
  }

  private drawSorcerer(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Powerful mage
    // Robes (blue/purple)
    ctx.fillStyle = '#4444aa';
    ctx.fillRect(x + 9, y + 14, 14, 14);

    // Hood and face
    ctx.fillStyle = '#5555bb';
    ctx.fillRect(x + 11, y + 6, 10, 10);

    // Face (visible, old)
    ctx.fillStyle = '#ddccbb';
    ctx.fillRect(x + 12, y + 8, 8, 8);

    // Eyes (wise, blue glow)
    ctx.fillStyle = '#00ccff';
    ctx.fillRect(x + 14, y + 11, 2, 2);
    ctx.fillRect(x + 18, y + 11, 2, 2);

    // Beard (long, white)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 13, y + 14, 6, 2);
    ctx.fillRect(x + 14, y + 16, 4, 4);
    ctx.fillRect(x + 15, y + 20, 2, 4);

    // Robe ornaments
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 13, y + 18, 2, 2);
    ctx.fillRect(x + 17, y + 18, 2, 2);
    ctx.fillRect(x + 15, y + 22, 2, 2);

    // Staff (ornate)
    ctx.fillStyle = '#8866ff';
    ctx.fillRect(x + 23, y + 6, 2, 20);
    // Crystal orb
    ctx.fillStyle = '#00ffff';
    ctx.fillRect(x + 22, y + 4, 4, 4);
    // Orb glow
    ctx.fillStyle = '#88ffff';
    ctx.fillRect(x + 21, y + 5, 1, 2);
    ctx.fillRect(x + 26, y + 5, 1, 2);

    // Magical energy
    ctx.fillStyle = '#aa88ff';
    ctx.fillRect(x + 7, y + 18, 1, 1);
    ctx.fillRect(x + 24, y + 14, 1, 1);
    ctx.fillRect(x + 10, y + 24, 1, 1);

    // Book (holding)
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 6, y + 20, 4, 5);
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 7, y + 21, 2, 1);
  }

  private drawDemon(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Greater demon
    // Body (red, muscular)
    ctx.fillStyle = '#aa0000';
    ctx.fillRect(x + 8, y + 12, 16, 16);

    // Head (horned)
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(x + 10, y + 6, 12, 8);

    // Large horns
    ctx.fillStyle = '#880000';
    ctx.fillRect(x + 9, y + 4, 3, 5);
    ctx.fillRect(x + 20, y + 4, 3, 5);
    ctx.fillRect(x + 8, y + 2, 3, 3);
    ctx.fillRect(x + 21, y + 2, 3, 3);

    // Eyes (glowing yellow)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 13, y + 9, 2, 2);
    ctx.fillRect(x + 17, y + 9, 2, 2);

    // Fangs (large)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 12, 2, 3);
    ctx.fillRect(x + 18, y + 12, 2, 3);

    // Wings (massive, bat-like)
    ctx.fillStyle = '#660000';
    ctx.fillRect(x + 2, y + 10, 7, 10);
    ctx.fillRect(x + 0, y + 12, 3, 8);
    ctx.fillRect(x + 23, y + 10, 7, 10);
    ctx.fillRect(x + 29, y + 12, 3, 8);

    // Wing bones
    ctx.fillStyle = '#440000';
    ctx.fillRect(x + 3, y + 12, 1, 6);
    ctx.fillRect(x + 5, y + 11, 1, 8);
    ctx.fillRect(x + 26, y + 12, 1, 6);
    ctx.fillRect(x + 28, y + 11, 1, 8);

    // Claws (hands)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 6, y + 18, 2, 1);
    ctx.fillRect(x + 24, y + 18, 2, 1);

    // Tail (spiked)
    ctx.fillStyle = '#aa0000';
    ctx.fillRect(x + 12, y + 26, 2, 4);
    ctx.fillRect(x + 11, y + 29, 1, 2);
    ctx.fillStyle = '#880000';
    ctx.fillRect(x + 10, y + 30, 3, 2);

    // Legs (hooved)
    ctx.fillStyle = '#aa0000';
    ctx.fillRect(x + 10, y + 26, 5, 5);
    ctx.fillRect(x + 17, y + 26, 5, 5);
  }

  // =====================
  // THE FOUR FIENDS + CHAOS (39-43)
  // =====================

  private drawLich(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Fiend of Earth - Undead sorcerer
    // Robes (dark purple/black)
    ctx.fillStyle = '#220044';
    ctx.fillRect(x + 8, y + 14, 16, 14);

    // Tattered robe edges
    ctx.fillStyle = '#110022';
    ctx.fillRect(x + 7, y + 24, 2, 4);
    ctx.fillRect(x + 23, y + 24, 2, 4);
    ctx.fillRect(x + 9, y + 26, 2, 2);
    ctx.fillRect(x + 21, y + 26, 2, 2);

    // Skeletal body visible
    ctx.fillStyle = '#ddddcc';
    ctx.fillRect(x + 13, y + 18, 6, 8);

    // Skull (crown)
    ctx.fillStyle = '#eeeecc';
    ctx.fillRect(x + 11, y + 4, 10, 12);

    // Crown of bones
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 10, y + 3, 12, 2);
    ctx.fillRect(x + 11, y + 1, 2, 2);
    ctx.fillRect(x + 15, y + 0, 2, 3);
    ctx.fillRect(x + 19, y + 1, 2, 2);

    // Eye sockets (glowing)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 13, y + 8, 2, 3);
    ctx.fillRect(x + 17, y + 8, 2, 3);

    // Teeth/jaw
    ctx.fillStyle = '#ccccaa';
    ctx.fillRect(x + 12, y + 13, 8, 2);
    ctx.fillRect(x + 13, y + 14, 1, 1);
    ctx.fillRect(x + 15, y + 14, 1, 1);
    ctx.fillRect(x + 17, y + 14, 1, 1);
    ctx.fillRect(x + 19, y + 14, 1, 1);

    // Staff (ornate, glowing)
    ctx.fillStyle = '#8844aa';
    ctx.fillRect(x + 23, y + 6, 2, 22);
    // Skull on top
    ctx.fillStyle = '#eeeecc';
    ctx.fillRect(x + 22, y + 3, 4, 4);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 23, y + 4, 1, 1);
    ctx.fillRect(x + 25, y + 4, 1, 1);

    // Magical aura
    ctx.fillStyle = '#8844aa';
    ctx.fillRect(x + 6, y + 16, 1, 1);
    ctx.fillRect(x + 25, y + 18, 1, 1);
    ctx.fillRect(x + 10, y + 26, 1, 1);
    ctx.fillRect(x + 21, y + 26, 1, 1);

    // Skeletal hands
    ctx.fillStyle = '#ddddcc';
    ctx.fillRect(x + 6, y + 20, 3, 4);
    ctx.fillRect(x + 23, y + 24, 3, 4);

    // Dark energy
    ctx.fillStyle = '#440088';
    ctx.fillRect(x + 12, y + 22, 8, 1);
    ctx.fillRect(x + 14, y + 24, 4, 1);
  }

  private drawKary(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Fiend of Fire - Multi-armed demon
    // Body (red/orange)
    ctx.fillStyle = '#ff4400';
    ctx.fillRect(x + 10, y + 12, 12, 16);

    // Head (demonic)
    ctx.fillStyle = '#ff6622';
    ctx.fillRect(x + 11, y + 6, 10, 8);

    // Horns (curved, ram-like)
    ctx.fillStyle = '#cc3300';
    ctx.fillRect(x + 10, y + 5, 2, 4);
    ctx.fillRect(x + 20, y + 5, 2, 4);
    ctx.fillRect(x + 9, y + 3, 2, 3);
    ctx.fillRect(x + 21, y + 3, 2, 3);
    ctx.fillRect(x + 8, y + 4, 2, 2);
    ctx.fillRect(x + 22, y + 4, 2, 2);

    // Eyes (flames)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 13, y + 9, 2, 2);
    ctx.fillRect(x + 17, y + 9, 2, 2);

    // Multiple arms (6 arms)
    ctx.fillStyle = '#ff4400';
    // Top arms
    ctx.fillRect(x + 6, y + 14, 5, 4);
    ctx.fillRect(x + 21, y + 14, 5, 4);
    // Middle arms
    ctx.fillRect(x + 5, y + 18, 6, 4);
    ctx.fillRect(x + 21, y + 18, 6, 4);
    // Bottom arms
    ctx.fillRect(x + 6, y + 22, 5, 4);
    ctx.fillRect(x + 21, y + 22, 5, 4);

    // Hands holding weapons
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 4, y + 16, 2, 2);
    ctx.fillRect(x + 26, y + 16, 2, 2);
    ctx.fillRect(x + 3, y + 20, 2, 2);
    ctx.fillRect(x + 27, y + 20, 2, 2);

    // Swords (flame swords)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 2, y + 14, 2, 6);
    ctx.fillRect(x + 28, y + 14, 2, 6);
    ctx.fillRect(x + 1, y + 18, 2, 6);
    ctx.fillRect(x + 29, y + 18, 2, 6);

    // Fire aura
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 9, y + 10, 1, 1);
    ctx.fillRect(x + 22, y + 10, 1, 1);
    ctx.fillRect(x + 8, y + 24, 1, 1);
    ctx.fillRect(x + 23, y + 24, 1, 1);

    // Legs
    ctx.fillStyle = '#ff4400';
    ctx.fillRect(x + 11, y + 26, 4, 5);
    ctx.fillRect(x + 17, y + 26, 4, 5);

    // Flames
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 10, y + 29, 1, 2);
    ctx.fillRect(x + 21, y + 29, 1, 2);
  }

  private drawKraken(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Fiend of Water - Massive sea monster (larger sprite: 48x48)
    // Head/body (blue-gray)
    ctx.fillStyle = '#226688';
    ctx.fillRect(x + 14, y + 8, 20, 16);

    // Eyes (large, yellow)
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 18, y + 12, 4, 4);
    ctx.fillRect(x + 26, y + 12, 4, 4);
    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 20, y + 14, 1, 1);
    ctx.fillRect(x + 28, y + 14, 1, 1);

    // Beak (center)
    ctx.fillStyle = '#334455';
    ctx.fillRect(x + 22, y + 18, 4, 4);

    // Tentacles (8 tentacles, massive)
    ctx.fillStyle = '#2277aa';
    // Left side tentacles
    ctx.fillRect(x + 4, y + 12, 12, 4);
    ctx.fillRect(x + 2, y + 14, 4, 2);
    ctx.fillRect(x + 6, y + 18, 10, 4);
    ctx.fillRect(x + 4, y + 20, 4, 2);
    ctx.fillRect(x + 8, y + 24, 8, 4);
    ctx.fillRect(x + 6, y + 26, 4, 2);

    // Right side tentacles
    ctx.fillRect(x + 32, y + 12, 12, 4);
    ctx.fillRect(x + 42, y + 14, 4, 2);
    ctx.fillRect(x + 32, y + 18, 10, 4);
    ctx.fillRect(x + 40, y + 20, 4, 2);
    ctx.fillRect(x + 32, y + 24, 8, 4);
    ctx.fillRect(x + 38, y + 26, 4, 2);

    // Bottom tentacles
    ctx.fillRect(x + 16, y + 22, 6, 8);
    ctx.fillRect(x + 26, y + 22, 6, 8);

    // Suckers on tentacles
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 8, y + 14, 2, 1);
    ctx.fillRect(x + 12, y + 14, 2, 1);
    ctx.fillRect(x + 10, y + 20, 2, 1);
    ctx.fillRect(x + 18, y + 26, 2, 1);
    ctx.fillRect(x + 36, y + 14, 2, 1);
    ctx.fillRect(x + 40, y + 14, 2, 1);
    ctx.fillRect(x + 36, y + 20, 2, 1);
    ctx.fillRect(x + 28, y + 26, 2, 1);

    // Water splashes
    ctx.fillStyle = '#88ccff';
    ctx.fillRect(x + 12, y + 10, 2, 2);
    ctx.fillRect(x + 34, y + 10, 2, 2);
    ctx.fillRect(x + 6, y + 28, 2, 1);
    ctx.fillRect(x + 40, y + 28, 2, 1);

    // Dark shadows (depth)
    ctx.fillStyle = '#113355';
    ctx.fillRect(x + 16, y + 20, 16, 2);
    ctx.fillRect(x + 20, y + 14, 8, 1);
  }

  private drawTiamat(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Fiend of Air - 5-headed dragon (larger sprite: 48x48)
    // Central body (purple/dark)
    ctx.fillStyle = '#6644aa';
    ctx.fillRect(x + 16, y + 16, 16, 12);

    // Central head (largest, center)
    ctx.fillStyle = '#7755bb';
    ctx.fillRect(x + 20, y + 8, 8, 10);
    ctx.fillRect(x + 24, y + 6, 4, 4);

    // Left head 1 (up-left)
    ctx.fillStyle = '#ff4400';
    ctx.fillRect(x + 10, y + 10, 6, 6);
    ctx.fillRect(x + 12, y + 8, 3, 3);

    // Left head 2 (down-left)
    ctx.fillStyle = '#4488ff';
    ctx.fillRect(x + 8, y + 18, 6, 6);
    ctx.fillRect(x + 10, y + 20, 3, 3);

    // Right head 1 (up-right)
    ctx.fillStyle = '#44aa44';
    ctx.fillRect(x + 32, y + 10, 6, 6);
    ctx.fillRect(x + 33, y + 8, 3, 3);

    // Right head 2 (down-right)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 34, y + 18, 6, 6);
    ctx.fillRect(x + 35, y + 20, 3, 3);

    // Eyes on all heads
    ctx.fillStyle = '#ffff00';
    // Center
    ctx.fillRect(x + 22, y + 11, 1, 1);
    ctx.fillRect(x + 25, y + 11, 1, 1);
    // Left 1
    ctx.fillRect(x + 12, y + 12, 1, 1);
    // Left 2
    ctx.fillRect(x + 10, y + 20, 1, 1);
    // Right 1
    ctx.fillRect(x + 35, y + 12, 1, 1);
    // Right 2
    ctx.fillRect(x + 37, y + 20, 1, 1);

    // Wings (massive)
    ctx.fillStyle = '#553388';
    ctx.fillRect(x + 4, y + 14, 8, 10);
    ctx.fillRect(x + 2, y + 16, 4, 8);
    ctx.fillRect(x + 36, y + 14, 8, 10);
    ctx.fillRect(x + 42, y + 16, 4, 8);

    // Wing details
    ctx.fillStyle = '#442277';
    ctx.fillRect(x + 5, y + 16, 1, 6);
    ctx.fillRect(x + 7, y + 15, 1, 8);
    ctx.fillRect(x + 42, y + 16, 1, 6);
    ctx.fillRect(x + 40, y + 15, 1, 8);

    // Tail (serpentine)
    ctx.fillStyle = '#6644aa';
    ctx.fillRect(x + 20, y + 26, 8, 4);
    ctx.fillRect(x + 22, y + 29, 4, 3);

    // Legs
    ctx.fillRect(x + 18, y + 26, 4, 6);
    ctx.fillRect(x + 26, y + 26, 4, 6);

    // Breath weapons (multiple colors)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 26, y + 8, 1, 1);
    ctx.fillStyle = '#00ffff';
    ctx.fillRect(x + 14, y + 10, 1, 1);
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(x + 36, y + 10, 1, 1);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 24, y + 6, 1, 1);

    // Crown/horns on central head
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 21, y + 6, 2, 2);
    ctx.fillRect(x + 25, y + 6, 2, 2);
  }

  private drawChaos(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Final Boss - Ultimate chaos entity (larger sprite: 64x64)
    // Central body (dark purple/black with multicolor aura)
    ctx.fillStyle = '#220033';
    ctx.fillRect(x + 20, y + 16, 24, 24);

    // Head (horned, demonic)
    ctx.fillStyle = '#330044';
    ctx.fillRect(x + 24, y + 8, 16, 12);

    // Massive horns
    ctx.fillStyle = '#110022';
    ctx.fillRect(x + 22, y + 6, 4, 8);
    ctx.fillRect(x + 38, y + 6, 4, 8);
    ctx.fillRect(x + 20, y + 4, 4, 6);
    ctx.fillRect(x + 40, y + 4, 4, 6);
    ctx.fillRect(x + 18, y + 2, 4, 4);
    ctx.fillRect(x + 42, y + 2, 4, 4);

    // Eyes (glowing red/purple)
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(x + 27, y + 12, 3, 3);
    ctx.fillRect(x + 34, y + 12, 3, 3);
    // Eye glow
    ctx.fillStyle = '#ff66ff';
    ctx.fillRect(x + 26, y + 13, 1, 1);
    ctx.fillRect(x + 30, y + 13, 1, 1);
    ctx.fillRect(x + 33, y + 13, 1, 1);
    ctx.fillRect(x + 37, y + 13, 1, 1);

    // Crown of chaos
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 26, y + 6, 2, 2);
    ctx.fillRect(x + 30, y + 4, 4, 3);
    ctx.fillRect(x + 36, y + 6, 2, 2);

    // Four wings (representing the Four Fiends)
    // Top wings
    ctx.fillStyle = '#440066';
    ctx.fillRect(x + 10, y + 12, 12, 12);
    ctx.fillRect(x + 6, y + 14, 6, 10);
    ctx.fillRect(x + 42, y + 12, 12, 12);
    ctx.fillRect(x + 52, y + 14, 6, 10);

    // Bottom wings
    ctx.fillRect(x + 12, y + 28, 10, 10);
    ctx.fillRect(x + 8, y + 32, 6, 8);
    ctx.fillRect(x + 42, y + 28, 10, 10);
    ctx.fillRect(x + 50, y + 32, 6, 8);

    // Wing details (veins)
    ctx.fillStyle = '#660088';
    ctx.fillRect(x + 12, y + 16, 1, 6);
    ctx.fillRect(x + 14, y + 14, 1, 8);
    ctx.fillRect(x + 51, y + 16, 1, 6);
    ctx.fillRect(x + 49, y + 14, 1, 8);

    // Armored chest
    ctx.fillStyle = '#440055';
    ctx.fillRect(x + 26, y + 22, 12, 12);
    // Armor plates
    ctx.fillStyle = '#550066';
    ctx.fillRect(x + 28, y + 24, 8, 1);
    ctx.fillRect(x + 28, y + 28, 8, 1);
    ctx.fillRect(x + 28, y + 32, 8, 1);

    // Chaos symbol on chest
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(x + 30, y + 26, 4, 1);
    ctx.fillRect(x + 31, y + 24, 2, 4);
    ctx.fillRect(x + 30, y + 30, 1, 1);
    ctx.fillRect(x + 33, y + 30, 1, 1);

    // Multiple arms (4 arms)
    ctx.fillStyle = '#330044';
    ctx.fillRect(x + 14, y + 24, 8, 6);
    ctx.fillRect(x + 42, y + 24, 8, 6);
    ctx.fillRect(x + 16, y + 32, 6, 6);
    ctx.fillRect(x + 42, y + 32, 6, 6);

    // Claws (massive)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 28, 3, 1);
    ctx.fillRect(x + 13, y + 29, 2, 2);
    ctx.fillRect(x + 49, y + 28, 3, 1);
    ctx.fillRect(x + 49, y + 29, 2, 2);

    // Weapons in hands
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 10, y + 26, 4, 8);
    ctx.fillRect(x + 50, y + 26, 4, 8);

    // Legs (demonic)
    ctx.fillStyle = '#220033';
    ctx.fillRect(x + 24, y + 38, 7, 10);
    ctx.fillRect(x + 33, y + 38, 7, 10);

    // Hooves
    ctx.fillStyle = '#110022';
    ctx.fillRect(x + 23, y + 46, 8, 2);
    ctx.fillRect(x + 33, y + 46, 8, 2);

    // Chaotic aura (multicolor)
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 18, y + 20, 1, 1);
    ctx.fillRect(x + 45, y + 36, 1, 1);
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(x + 44, y + 18, 1, 1);
    ctx.fillRect(x + 19, y + 38, 1, 1);
    ctx.fillStyle = '#0000ff';
    ctx.fillRect(x + 20, y + 42, 1, 1);
    ctx.fillRect(x + 43, y + 24, 1, 1);
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(x + 46, y + 28, 1, 1);
    ctx.fillRect(x + 17, y + 30, 1, 1);

    // Energy crackling
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(x + 16, y + 16, 1, 1);
    ctx.fillRect(x + 47, y + 16, 1, 1);
    ctx.fillRect(x + 22, y + 44, 1, 1);
    ctx.fillRect(x + 41, y + 44, 1, 1);
  }
}

// Export singleton
let enhancedEnemySpritesInstance: EnhancedEnemySprites | null = null;

export function getEnhancedEnemySprites(): EnhancedEnemySprites {
  if (!enhancedEnemySpritesInstance) {
    enhancedEnemySpritesInstance = new EnhancedEnemySprites();
  }
  return enhancedEnemySpritesInstance;
}
